<template>
  <div class="comp-effective-rent-tab">
    <div class="comp-effective-rent-tab__header">
      <div class="comp-effective-rent-tab__header-icon">
        <theme-icon name="calculator" type="icons" classes="w-6 h-6" />
      </div>
      <div>
        <h2 class="comp-effective-rent-tab__title">Effective Rent Calculator</h2>
        <p class="comp-effective-rent-tab__subtitle">Calculate 1st year effective rent with amortization</p>
      </div>
    </div>

    <!-- Metric Cards -->
    <div class="comp-effective-rent-tab__metrics">
      <div class="comp-effective-rent-tab__metric-card comp-effective-rent-tab__metric-card--primary">
        <div class="comp-effective-rent-tab__metric-icon">
          <theme-icon name="money" type="icons" classes="w-5 h-5" />
        </div>
        <div>
          <div class="comp-effective-rent-tab__metric-label">Effective Rent PSF</div>
          <div class="comp-effective-rent-tab__metric-value">{{ formatCurrency(effectiveRentPSF, 2) }}</div>
        </div>
      </div>

      <div class="comp-effective-rent-tab__metric-card comp-effective-rent-tab__metric-card--primary">
        <div class="comp-effective-rent-tab__metric-icon">
          <theme-icon name="arrow-up" type="icons" classes="w-5 h-5" />
        </div>
        <div>
          <div class="comp-effective-rent-tab__metric-label">1st Year Total</div>
          <div class="comp-effective-rent-tab__metric-value">{{ formatCurrency(effectiveRentYear) }}</div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="comp-effective-rent-tab__content">
      <!-- Left Section: Input Forms -->
      <div class="comp-effective-rent-tab__inputs">
        <!-- Space Requirements -->
        <div class="comp-effective-rent-tab__section">
          <div class="comp-effective-rent-tab__section-header">
            <theme-icon name="building" type="icons" classes="w-4 h-4" />
            <h3 class="comp-effective-rent-tab__section-title">Space Requirements</h3>
          </div>

          <div class="comp-effective-rent-tab__form-group">
            <label class="comp-effective-rent-tab__label">USABLE SQ. FT.</label>
            <themed-input
              v-model="usableSquareFeetRequired"
              type="number"
              placeholder="0"
            />
          </div>

          <div class="comp-effective-rent-tab__form-group">
            <label class="comp-effective-rent-tab__label">LOSS FACTOR %</label>
            <themed-input
              v-model="estimatedLossFactor"
              type="text"
              placeholder="0%"
            />
          </div>

          <div class="comp-effective-rent-tab__form-group">
            <label class="comp-effective-rent-tab__label">Rentable Sq. Ft.</label>
            <div class="comp-effective-rent-tab__readonly-value">{{ rentableSquareFeet | number }}</div>
          </div>
        </div>

        <!-- Financial Terms -->
        <div class="comp-effective-rent-tab__section">
          <div class="comp-effective-rent-tab__section-header">
            <theme-icon name="money" type="icons" classes="w-4 h-4" />
            <h3 class="comp-effective-rent-tab__section-title">Financial Terms</h3>
          </div>

          <div class="comp-effective-rent-tab__form-group">
            <label class="comp-effective-rent-tab__label">BASE RENT PSF</label>
            <themed-input
              v-model="baseRent"
              type="text"
              placeholder="$0.00"
            />
          </div>

          <div class="comp-effective-rent-tab__form-group">
            <label class="comp-effective-rent-tab__label">WORK REQUIRED TOTAL</label>
            <themed-input
              v-model="workRequiredTotal"
              type="text"
              placeholder="$0"
            />
          </div>

          <div class="comp-effective-rent-tab__form-group">
            <label class="comp-effective-rent-tab__label">LANDLORD CONTRIB. PRSF</label>
            <themed-input
              v-model="landlordContributionPRSF"
              type="text"
              placeholder="$0.00"
            />
          </div>

          <div class="comp-effective-rent-tab__form-row">
            <div class="comp-effective-rent-tab__form-group">
              <label class="comp-effective-rent-tab__label">Work Req. PUSF</label>
              <div class="comp-effective-rent-tab__readonly-value">{{ formatCurrency(workRequiredPUSF, 2) }}</div>
            </div>

            <div class="comp-effective-rent-tab__form-group">
              <label class="comp-effective-rent-tab__label">Est. Work PRSF</label>
              <div class="comp-effective-rent-tab__readonly-value">{{ formatCurrency(workRequiredPRSF, 2) }}</div>
            </div>
          </div>
        </div>

        <!-- Lease Terms -->
        <div class="comp-effective-rent-tab__section">
          <div class="comp-effective-rent-tab__section-header">
            <theme-icon name="document-text" type="icons" classes="w-4 h-4" />
            <h3 class="comp-effective-rent-tab__section-title">Lease Terms</h3>
          </div>

          <div class="comp-effective-rent-tab__form-group">
            <label class="comp-effective-rent-tab__label">LEASE TERM (YEARS)</label>
            <themed-input
              v-model="leaseTermYears"
              type="number"
              placeholder="0"
            />
          </div>

          <div class="comp-effective-rent-tab__form-group">
            <label class="comp-effective-rent-tab__label">FREE RENT (MONTHS)</label>
            <themed-input
              v-model="freeRentMonths"
              type="number"
              placeholder="0"
            />
          </div>

          <div class="comp-effective-rent-tab__form-group">
            <label class="comp-effective-rent-tab__label">ESCALATION %</label>
            <themed-input
              v-model="escalationPercentage"
              type="text"
              placeholder="0%"
            />
          </div>

          <div class="comp-effective-rent-tab__form-group">
            <label class="comp-effective-rent-tab__label">INTEREST RATE %</label>
            <themed-input
              v-model="interestForAmortizationPercentage"
              type="text"
              placeholder="0%"
            />
          </div>
        </div>
      </div>

      <!-- Right Section: Summary -->
      <div class="comp-effective-rent-tab__summary">
        <div class="comp-effective-rent-tab__summary-item">
          <div class="comp-effective-rent-tab__summary-label">WORK REQ AMORTIZE PRSF</div>
          <div class="comp-effective-rent-tab__summary-value">{{ formatCurrency(workToAmortizePRSF, 2) }}</div>
        </div>

        <div class="comp-effective-rent-tab__summary-item">
          <div class="comp-effective-rent-tab__summary-label">TOTAL WORK TO AMORTIZE</div>
          <div class="comp-effective-rent-tab__summary-value">{{ formatCurrency(workToAmortizeTotal) }}</div>
        </div>

        <div class="comp-effective-rent-tab__summary-item">
          <div class="comp-effective-rent-tab__summary-label">WORK AMORTIZE PSF</div>
          <div class="comp-effective-rent-tab__summary-value">{{ formatCurrency(workToAmortizePSF, 2) }}</div>
        </div>

        <div class="comp-effective-rent-tab__summary-item">
          <div class="comp-effective-rent-tab__summary-label">BASE RENT + WORK</div>
          <div class="comp-effective-rent-tab__summary-value">{{ formatCurrency(baseRentAndWork, 2) }}</div>
        </div>

        <themed-button
          @click="save"
          variant="primary"
          class="comp-effective-rent-tab__save-button"
        >
          Save Calculator
        </themed-button>
      </div>
    </div>
  </div>
</template>

<script>
import ThemedButton from '../ThemedButton.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
  name: 'CompEffectiveRentTab',

  components: {
    ThemedButton,
    ThemedInput,
    ThemeIcon
  },

  props: {
    projectId: {
      type: Number,
      required: true
    },

    data: {
      type: [Object, Array],
      required: true
    },

    fromReatlas: {
      type: Boolean,
      default: false
    }
  },

  data() {
    const calcData = this.data || {};
    return {
      workRequiredTotal: calcData.work_required_total || 0,
      landlordContributionPRSF: calcData.landlord_contribution_prsf || 0,
      usableSquareFeetRequired: calcData.usable_square_feet_required || 0,
      estimatedLossFactor: calcData.estimated_loss_factor_percentage || 0,
      baseRent: calcData.base_rent || 0,
      leaseTermYears: calcData.lease_term_years || 0,
      freeRentMonths: calcData.free_rent_months || 0,
      escalationPercentage: calcData.escalation_percentage || 0,
      interestForAmortizationPercentage: calcData.interest_amortization_percentage || 0
    };
  },

  computed: {
    leaseTermMonths() {
      return this.leaseTermYears * 12;
    },

    lossFactor() {
      return parseInt(this.estimatedLossFactor) / 100;
    },

    rentableSquareFeet() {
      const usableSquareFeetRequired = Number.prototype.cleanseInteger(this.usableSquareFeetRequired);

      return isNaN(usableSquareFeetRequired) || isNaN(this.lossFactor)
        ? 0
        : parseInt(Math.round(usableSquareFeetRequired / (1 - this.lossFactor)));
    },

    workRequiredPUSF() {
      const usableSquareFeetRequired = Number.prototype.cleanseInteger(this.usableSquareFeetRequired);
      const workRequiredTotal = Number.prototype.cleanseInteger(this.workRequiredTotal);

      return isNaN(usableSquareFeetRequired) || isNaN(workRequiredTotal)
        ? 0
        : parseFloat((workRequiredTotal / usableSquareFeetRequired).toFixed(2) * 100);
    },

    workRequiredPRSF() {
      const workRequiredTotal = Number.prototype.cleanseInteger(this.workRequiredTotal);

      return isNaN(this.rentableSquareFeet) || isNaN(workRequiredTotal)
        ? 0
        : parseFloat((workRequiredTotal / this.rentableSquareFeet).toFixed(2) * 100);
    },

    workToAmortizePRSF() {
      const landlordContributionPRSF = Number.prototype.cleanseFloat(this.landlordContributionPRSF);

      return isNaN(this.workRequiredPRSF) || isNaN(landlordContributionPRSF)
        ? 0
        : parseFloat(Math.abs((this.workRequiredPRSF - landlordContributionPRSF * 100).toFixed(2)));
    },

    workToAmortizeTotal() {
      return isNaN(this.workToAmortizePRSF) || isNaN(this.rentableSquareFeet)
        ? 0
        : parseFloat(Math.round(this.workToAmortizePRSF * this.rentableSquareFeet));
    },

    workToAmortizePSF() {
      if (this.rentableSquareFeet == 0 || this.workToAmortizeTotal == 0 || isNaN(this.leaseTermMonths)) {
        return 0;
      }

      const freeRentMonths = isNaN(this.freeRentMonths) ? 0 : parseInt(this.freeRentMonths);

      const pmt = Number.prototype.pmt(
        this.workToAmortizeTotal / 100,
        this.interestForAmortizationPercentage,
        this.leaseTermMonths + freeRentMonths
      );

      return parseFloat(((pmt * 12) / this.rentableSquareFeet).toFixed(2) * 100);
    },

    baseRentAndWork() {
      const baseRent = Number.prototype.cleanseFloat(this.baseRent);

      return isNaN(baseRent) || isNaN(this.workToAmortizePSF)
        ? 0
        : parseFloat((baseRent * 100 + this.workToAmortizePSF).toFixed(2));
    },

    freeRentPSF() {
      const baseRent = Number.prototype.cleanseFloat(this.baseRent);

      return isNaN(this.leaseTermMonths)
        ? 0
        : parseFloat((this.freeRentMonths / (this.leaseTermMonths + parseInt(this.freeRentMonths)) * baseRent) * 100);
    },

    effectiveRentPSF() {
      return parseFloat(this.baseRentAndWork - this.freeRentPSF);
    },

    effectiveRentYear() {
      return parseFloat((this.effectiveRentPSF * this.rentableSquareFeet).toFixed(2));
    }
  },

  methods: {
    save() {
      const compId = this.data && this.data.comp_id ? this.data.comp_id : null;
      
      if (!compId) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Unable to save: Comp ID is missing'
        });
        return;
      }

      axios
        .post('/projects/' + this.projectId + '/comps/' + compId + '/effective-rent-calculation', {
          comp_id: compId,
          usable_square_feet_required: Number.prototype.cleanseInteger(this.usableSquareFeetRequired),
          rentable_square_feet: this.rentableSquareFeet,
          estimated_loss_factor_percentage: Number.prototype.cleanseFloat(this.estimatedLossFactor),
          base_rent: this.baseRent,
          lease_term_years: this.leaseTermYears,
          free_rent_months: this.freeRentMonths,
          free_rent_psf: this.freeRentPSF,
          interest_amortization_percentage: Number.prototype.cleanseFloat(this.interestForAmortizationPercentage),
          escalation_percentage: Number.prototype.cleanseFloat(this.escalationPercentage),
          landlord_contribution_prsf: this.landlordContributionPRSF,
          work_required_total: Number.prototype.cleanseInteger(this.workRequiredTotal),
          work_required_pusf: this.workRequiredPUSF,
          estimated_work_required_prsf: this.workRequiredPRSF,
          estimated_work_amortize_prsf: this.workToAmortizePRSF,
          total_work_to_amortize: this.workToAmortizeTotal,
          work_amortize_psf: this.workToAmortizePSF,
          base_rent_plus_work: this.baseRentAndWork,
          effective_rent_psf: this.effectiveRentPSF,
          effective_rent_total: this.effectiveRentYear
        })
        .then(response => {
          this.$notify({
            type: 'success',
            title: 'Success',
            text: response.data.message
          });
        })
        .catch(response => {
          console.error(response);

          this.$notify({
            type: 'error',
            title: 'Error',
            text: response.data.message
          });
        });
    }
  }
};
</script>
